/* CustomerDashboard — /dashboard
 *
 * Real customer dashboard backed by /api/customer/purchases. Shows the
 * customer's purchased Concept Plans, payment status, and re-download
 * links. Replaces the legacy demo UserDashboard.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Download,
  ShieldCheck,
  ChevronRight,
  Sparkles,
  Smartphone,
  LogOut,
  Loader2,
  RefreshCw,
  Receipt,
  Inbox,
  Clock3,
  CheckCircle2,
  XCircle,
  ShoppingBag,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import BrandLogo from "@/components/BrandLogo";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import { BudgetDetailedReport } from "@/components/BudgetEstimateCard";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const STATUS_META = {
  verified: { label: "Verified · Unlocked", tone: "ok", icon: CheckCircle2 },
  screenshot_uploaded: { label: "Awaiting verification", tone: "wait", icon: Clock3 },
  suspicious: { label: "Flagged — contact us", tone: "warn", icon: XCircle },
  pending: { label: "Payment pending", tone: "wait", icon: Clock3 },
  failed: { label: "Payment failed", tone: "warn", icon: XCircle },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { label: status || "Unknown", tone: "wait", icon: Clock3 };
  const Icon = meta.icon;
  const palette =
    meta.tone === "ok"
      ? "bg-emerald-100 text-emerald-700 border-emerald-300"
      : meta.tone === "warn"
      ? "bg-red-100 text-red-700 border-red-300"
      : "bg-yellow-100 text-yellow-800 border-yellow-300";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-extrabold uppercase tracking-wider ${palette}`}
      data-testid={`purchase-status-${status}`}
    >
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
};

const PurchaseCard = ({ purchase }) => {
  const planFiles =
    purchase.plan_files && purchase.plan_files.length > 0
      ? purchase.plan_files
      : purchase.plan_url
      ? [{ label: "Concept Plan", url: purchase.plan_url }]
      : [];
  const unlocked = purchase.payment_status === "verified" && planFiles.length > 0;
  const paidOn = purchase.paid_at ? new Date(purchase.paid_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";
  return (
    <article
      className="rounded-3xl bg-white border-2 border-border overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col"
      data-testid={`purchase-card-${purchase.id}`}
    >
      <div className="p-4 sm:p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-[16px] font-extrabold tracking-tight text-navy truncate">
              {purchase.design_name}
            </h3>
            <div className="text-[12.5px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
              <span>{purchase.design_size}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Receipt className="h-3 w-3" />
                {purchase.receipt_no}
              </span>
              {paidOn && (
                <>
                  <span>·</span>
                  <span>{paidOn}</span>
                </>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold">
              Amount
            </div>
            <div className="text-lg font-extrabold text-gold-dark tabular-nums">
              {formatINR(purchase.amount)}
            </div>
          </div>
        </div>
        <StatusPill status={purchase.payment_status} />
        {unlocked ? (
          <div className="grid grid-cols-1 gap-1.5">
            {planFiles.map((f, i) => (
              <a
                key={f.label + i}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`purchase-download-${purchase.id}-${i}`}
                className="min-h-[44px] rounded-xl bg-navy text-gold font-extrabold text-[12.5px] flex items-center justify-center gap-1.5 hover:bg-navy-deep transition-colors"
              >
                <Download className="h-3.5 w-3.5" /> {f.label || `Concept Plan ${i + 1}`}
              </a>
            ))}
          </div>
        ) : (
          <div
            className="rounded-xl border-2 border-dashed border-border bg-sand-alt px-3 py-3 text-[12.5px] text-muted-foreground leading-snug"
            data-testid={`purchase-locked-${purchase.id}`}
          >
            {purchase.payment_status === "screenshot_uploaded"
              ? "We're verifying your payment. Plans unlock automatically — usually within an hour. We'll WhatsApp you the moment it's done."
              : "This plan is locked. Once your payment is verified, the download button appears here."}
          </div>
        )}
        {/* Detailed Tentative Construction + EMI breakdown — visible only
            for verified/unlocked designs that carry a built-up area. */}
        {unlocked && purchase.area_sqft ? (
          <div data-testid={`purchase-budget-report-${purchase.id}`}>
            <BudgetDetailedReport
              area={purchase.area_sqft}
              floors={purchase.num_floors || 1}
            />
          </div>
        ) : null}
      </div>
    </article>
  );
};

const CustomerDashboard = () => {
  const { customer, logoutCustomer } = useAuth();
  const [purchases, setPurchases] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/customer/purchases`);
      setPurchases(data.items || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load your purchases. Please try again.");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const verified = useMemo(
    () => (purchases || []).filter((p) => p.payment_status === "verified"),
    [purchases]
  );

  const handleLogout = async () => {
    await logoutCustomer();
    window.location.assign("/");
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="customer-dashboard">
      <header className="sticky top-0 z-30 bg-navy text-white border-b-4 border-gold shadow-[0_8px_24px_-12px_rgba(11,37,69,0.4)]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/"
              data-testid="customer-home-link"
              aria-label="Home"
              className="inline-flex items-center bg-white rounded-lg px-2 py-1 shadow-sm shrink-0"
            >
              <BrandLogo size="sm" asLink={false} withTagline lang="en" testId="customer-brand-logo" />
            </Link>
            <div className="leading-tight min-w-0">
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-bold truncate">
                My Dashboard
              </div>
              <div className="text-[15px] font-extrabold tracking-tight truncate">
                {customer?.name || "Welcome back"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/store"
              data-testid="customer-browse-store"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gold text-navy text-sm font-extrabold border-2 border-gold-dark hover:bg-gold-dark hover:text-white transition-colors"
            >
              <ShoppingBag className="h-4 w-4" /> Browse Library
            </Link>
            <Link
              to="/professionals"
              data-testid="customer-hire-professionals"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-bold border border-white/15"
            >
              <Sparkles className="h-4 w-4" /> Hire Professionals
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="customer-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/15 hover:bg-red-500/30 text-red-200 hover:text-white text-sm font-bold border border-red-400/30 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Profile pill */}
        <section
          className="rounded-3xl bg-navy-jaali text-white p-5 sm:p-6 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.55)] relative overflow-hidden"
          data-testid="customer-profile-card"
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
          <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-gold/10 blur-2xl pointer-events-none" />
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold text-[10.5px] font-bold tracking-widest uppercase">
            <Sparkles className="h-3 w-3" /> Mera Naksha Member
          </div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight">
            {customer?.name ? `नमस्ते, ${customer.name}` : "नमस्ते!"}
          </h1>
          <div className="mt-1 text-[13.5px] text-white/75 inline-flex items-center gap-1.5">
            <Smartphone className="h-3.5 w-3.5" /> +91 {customer?.phone}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <div
              className="rounded-2xl bg-white/8 border border-white/15 px-4 py-2.5"
              data-testid="stat-purchases-total"
            >
              <div className="text-[10.5px] uppercase tracking-wider text-gold font-bold">
                Total Plans
              </div>
              <div className="text-xl font-extrabold tabular-nums">
                {purchases === null ? "—" : purchases.length}
              </div>
            </div>
            <div
              className="rounded-2xl bg-white/8 border border-white/15 px-4 py-2.5"
              data-testid="stat-purchases-unlocked"
            >
              <div className="text-[10.5px] uppercase tracking-wider text-gold font-bold">
                Unlocked
              </div>
              <div className="text-xl font-extrabold tabular-nums">
                {purchases === null ? "—" : verified.length}
              </div>
            </div>
            <a
              href="https://wa.me/919717667115?text=Hi%20Mera%20Naksha%20team%2C%20I%20need%20help%20with%20my%20purchase."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="customer-whatsapp-support"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-[12.5px] font-extrabold hover:bg-emerald-600"
            >
              <FaWhatsapp className="h-4 w-4" /> Chat support
            </a>
          </div>
        </section>

        {/* Purchases */}
        <section data-testid="customer-purchases-section">
          <div className="flex items-end justify-between gap-3 mb-3">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-navy/70 font-extrabold">
                Order History
              </div>
              <h2 className="text-[18px] sm:text-xl font-extrabold tracking-tight text-navy">
                Your Concept Plans
              </h2>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={refreshing}
              data-testid="customer-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-border text-navy font-bold text-[12px] hover:border-navy disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          {error && (
            <div className="rounded-2xl bg-red-50 border-2 border-red-200 px-4 py-3 text-[13px] text-red-800 font-semibold">
              {error}
            </div>
          )}

          {purchases === null ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="rounded-3xl bg-white border-2 border-border p-5 animate-pulse">
                  <div className="h-5 bg-sand-alt rounded w-3/5" />
                  <div className="mt-2 h-4 bg-sand-alt rounded w-2/5" />
                  <div className="mt-5 h-10 bg-sand-alt rounded" />
                </div>
              ))}
            </div>
          ) : purchases.length === 0 ? (
            <div
              className="rounded-3xl bg-white border-2 border-dashed border-border p-10 text-center"
              data-testid="customer-empty-purchases"
            >
              <Inbox className="h-10 w-10 mx-auto text-navy/30" />
              <div className="mt-3 font-bold text-navy">No purchases yet</div>
              <div className="text-[13px] text-muted-foreground mt-1 max-w-md mx-auto">
                Once you buy a Concept Plan from the Mera Naksha Design Library, it'll appear right here for instant re-download.
              </div>
              <Link
                to="/store"
                data-testid="customer-empty-go-store"
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gold text-navy font-extrabold text-[13px] hover:bg-gold-dark hover:text-white"
              >
                Browse the Design Library <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {purchases.map((p) => (
                <PurchaseCard key={p.id} purchase={p} />
              ))}
            </div>
          )}
        </section>

        <section className="rounded-2xl bg-white border border-border p-4 flex items-start gap-3 shadow-sm">
          <div className="h-10 w-10 rounded-xl bg-navy text-gold grid place-items-center shrink-0">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="text-[13px] text-foreground">
            <div className="font-bold text-navy">Need help with a plan?</div>
            <div className="text-muted-foreground">
              WhatsApp us at <strong>+91 97176 67115</strong> with your receipt
              number. Most queries resolved in under an hour.
            </div>
          </div>
        </section>

        <LegalDisclaimer lang="en" variant="block" testId="customer-dashboard-disclaimer" />
        {refreshing && (
          <div
            className="inline-flex items-center gap-2 text-[12px] text-muted-foreground"
            data-testid="customer-refreshing"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Refreshing your purchases…
          </div>
        )}
      </main>
    </div>
  );
};

export default CustomerDashboard;
